import React, { useEffect, useMemo, useRef, useState } from 'react';
import { get } from 'lodash';
import Icon from '../Icon';
import { sortByNumberOrString } from './sorting';
import { ITableColumn } from 'src/components/Table/types';
import { IContactTableRow } from 'src/components/ContactsTable/data';
import { OccupancyReportData } from 'src/pages/Reporting/ReportingFormTypes';
import { IServiceRequest } from 'src/services/api/ServiceRequestDashboard/apiContracts';
import { ISuite } from 'src/components/SharedTypes';
import { UnitBulkOperationDto } from 'src/SharedCustomHooks/UsePropertySuites/types';
import { IWalkthroughModel } from 'src/services/client-types';
import { CommonArea } from 'src/SharedCustomHooks/UseCommonAreas/useCommonAreas';
import {
  CustomPagination,
  useClientPagination,
  usePersistedPageState,
} from 'src/components/CustomPagination';
import { InboxServiceRequestRow } from 'src/services/api/InboxServiceRequests/apiContracts';
import { IOutboxEmail } from 'src/services/api/EmailOutbox/apiContracts';
import TableHeader from './components/TableHeader/TableHeader';
import TableRow from './components/TableRow/TableRow';
import TableGroupRow from './components/TableGroupRow/TableGroupRow';
import { buildColumnTemplate, buildMinWidth } from './gridTemplate';
import './table.scss';
import { PageState } from 'src/components/CustomPagination/usePersistedPageState';

type TableRowDatum =
  | IContactTableRow
  | OccupancyReportData
  | IServiceRequest
  | ISuite
  | UnitBulkOperationDto
  | IWalkthroughModel
  | CommonArea
  | InboxServiceRequestRow
  | IOutboxEmail;

interface ITableProps {
  columns: ITableColumn[];
  data: TableRowDatum[];
  defaultSort?: {
    accessor: string;
    direction: 'ascending' | 'descending';
  };
  stickyHeader?: boolean;
  headerTop?: number | string;
  expandedGroups?: string[];
  editColumns?: boolean;
  /* Opt-in client-side pagination. When set, the table sorts the full data
   * first (so sort spans the whole list, not just the visible page), then
   * renders only the current page's slice with CustomPagination controls.
   * `position` defaults to 'bottom'; use 'both' for long tables where the
   * user shouldn't have to scroll back up to change pages. */
  paginate?: {
    itemsPerPage: number;
    position?: 'top' | 'bottom' | 'both';
    /* Opt-in: persist the current page number in redux under this key, so it
     * survives navigating away and back. Omit for local (per-mount) paging. */
    persistKey?: string;
  };
  /* Skeleton mode for the pagination chrome only. Keeps the same nav/list
   * footprint as the real pagination but renders each control as a Skeleton
   * rect, so swapping in real data is a clean fade and not a layout shift. */
  paginationSkeleton?: boolean;
  /* Opt-in: when provided, CustomPagination renders the gear icon that opens
   * the items-per-page dialogue, and the summary text "Showing X of Y items".
   * Callsites typically wire this through useTablePagination. */
  onItemsPerPageChange?: (next: number) => void;
}

const Table = ({
  columns,
  data,
  defaultSort,
  stickyHeader = false,
  headerTop,
  expandedGroups = [],
  editColumns,
  paginate,
  paginationSkeleton = false,
  onItemsPerPageChange,
}: ITableProps) => {
  const [sort, setSort] = useState(
    defaultSort || {
      accessor: '',
      direction: '',
    }
  );

  const { accessor, direction } = sort;

  const sortingAlgorithm =
    columns.find((c: ITableColumn) => c.accessor === accessor)
      ?.sortingAlgorithm || sortByNumberOrString;

  const sorted: TableRowDatum[] = useMemo(() => {
    const sortedData: TableRowDatum[] = data.slice().sort((a, b) => {
      const valA: any = get(a, accessor);
      const valB: any = get(b, accessor);
      return sortingAlgorithm(valA, valB);
    });

    return direction === 'ascending' ? sortedData.reverse() : sortedData;
  }, [data, accessor, sortingAlgorithm, direction]);

  const isPaginated: boolean = Boolean(paginate);
  const itemsPerPage: number = paginate?.itemsPerPage ?? 0;

  const persistedPageStateFromRedux: PageState = usePersistedPageState(
    paginate?.persistKey
  );

  const {
    currentPage,
    totalPages,
    paginatedItems,
    showBottomPagination,
    setCurrentPage,
  } = useClientPagination<any>({
    items: sorted as any[],
    itemsPerPage,
    position: paginate?.position,
    pageStateFromRedux: persistedPageStateFromRedux,
  });

  const displayedRows: any[] = isPaginated ? paginatedItems : (sorted as any[]);

  const paginationRef = useRef<HTMLDivElement>(null);
  const isFirstPaginationRender = useRef<boolean>(true);

  useEffect(() => {
    if (!isPaginated) {
      return;
    }

    if (isFirstPaginationRender.current) {
      isFirstPaginationRender.current = false;
      return;
    }

    paginationRef.current?.scrollIntoView({
      block: 'end',
      behavior: 'instant' as ScrollBehavior,
    });
  }, [currentPage, isPaginated]);

  const columnDefinitions: ITableColumn[] = useMemo(
    () =>
      [
        ...columns,
        editColumns
          ? {
              sortable: false,
              textOverflow: false,
              label: (
                <span
                  style={{
                    backgroundColor: '#1c1c1c',
                    color: '#fff',
                    padding: '.6rem 1.5rem',
                    borderRadius: '2rem',
                    cursor: 'pointer',
                  }}
                >
                  <Icon glyph="pen" weight="solid" />
                </span>
              ),
              width: '7rem',
            }
          : null,
      ].filter(Boolean) as ITableColumn[],
    [columns, editColumns]
  );

  const gridTemplateColumns: string = buildColumnTemplate(columnDefinitions);
  const minWidth: string | undefined = buildMinWidth(columnDefinitions);

  const tableElement: JSX.Element = (
    <div
      role="table"
      className="juniper-datatable"
      style={{ minWidth, gridTemplateColumns }}
    >
      <TableHeader
        columns={columnDefinitions}
        sort={sort}
        onSort={setSort}
        stickyHeader={stickyHeader}
        headerTop={headerTop}
      />
      <div role="rowgroup" className="juniper-datatable-rowgroup">
        {displayedRows.map((d: any, i: number) =>
          d.isGroup ? (
            <TableGroupRow
              key={`group-${d.id}-${i}`}
              data={d}
              columns={columnDefinitions}
              index={i}
              isExpanded={expandedGroups.includes(d.id)}
            />
          ) : (
            <TableRow
              key={`row-${d.id}-${i}`}
              data={d}
              columns={columnDefinitions}
              index={i}
            />
          )
        )}
      </div>
    </div>
  );

  const paginationControls: JSX.Element = (
    <div
      ref={paginationRef}
      style={{
        marginTop: '1.5rem',
        marginBottom: '0.75rem',
        scrollMarginBottom: '4rem',
      }}
    >
      <CustomPagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setCurrentPage}
        isSkeleton={paginationSkeleton}
        totalItems={sorted.length}
        currentItemsPerPage={itemsPerPage}
        onItemsPerPageChange={onItemsPerPageChange}
      />
    </div>
  );

  return (
    <>
      <div className="juniper-datatable-scroll">{tableElement}</div>
      {isPaginated && showBottomPagination && paginationControls}
    </>
  );
};

export default Table;